import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';

import { generateSampleDetailsParams } from '@features/filter/static/filterUtil';
import AssignSampleMember from '@features/sampleDevelopment/components/AssignSampleMember';

import { handleFilterOfListPage } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchSampleProductById } from '@store/actions/SampleAction';

import ApiClient from '@lib/apiClient';

import { checkPrimaryUserType, isPatternMaster, replaceSpace, STATIC_SET_CONSTANTS } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const SampleActionPopup = ({ selectedIds, closeModal, idFromSetDetails = null }) => {
	const [openModal, setOpenModal] = useState(false);
	const [actionType, setActionType] = useState('');
	const filterfields = useSelector((store) => store.filterFieldStore);
	const dispatch = useDispatch();
	const { id } = useParams();

	const handleClick = (value) => {
		if (value === 'Pattern' || value === 'Cutting' || value === 'Sewing') {
			setOpenModal(true);
		} else {
			let requestList = [];
			for (let item of selectedIds) {
				requestList.push({
					sampleActivityDTO: {
						activityType: replaceSpace(value)?.toUpperCase()
					},
					sampleItemId:
						item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
							? item?.setMemberSampleRequestItemResponseList?.map((set) => set.id)
							: [item.id]
				});
			}

			let requestBody = [];
			requestList.map((item) => {
				item.sampleItemId.map((id) => {
					requestBody.push({
						sampleItemId: id,
						sampleActivityDTO: item.sampleActivityDTO
					});
				});
			});
			dispatch(openLoader());
			ApiClient.PUT(
				`${BASE_URL}/sample/request/${idFromSetDetails ? idFromSetDetails : id}/update-item-activity`,
				requestBody,
				'',
				true
			)
				.then(({ data }) => {
					toastSuccess(data?.message);
					if (idFromSetDetails) {
						dispatch(fetchSampleProductById(idFromSetDetails ? idFromSetDetails : id));
					} else {
						const { ...paramsData } = generateSampleDetailsParams(filterfields);
						dispatch(
							handleFilterOfListPage(
								{ type: 'sampleDetailsAction', idUrl: `${id}` },
								paramsData,
								false
							)
						);
					}
					dispatch(closeLoader());
					closeModal();
				})
				.catch(({ response }) => {
					toastError(response.data.message);
					dispatch(closeLoader());
				});
		}
		setActionType(value);
	};

	const handleClose = () => {
		setOpenModal(false);
		setActionType('');
		closeModal();
	};

	const buttonNames = ['Pattern', 'Cutting', 'Sewing', 'Print', 'Wash', 'Embroidery', 'Delivered'];
	const factoryButtons = [
		'Handover to Factory',
		'Received from Factory',
		'Send to Buyer',
		'Received by Buyer',
		'Approved',
		'Rejected'
	];
	const inhouseButtons = ['Send to Buyer', 'Received by Buyer', 'Approved', 'Rejected'];
	const notSameError = 'You have to select styles from similar development points';
	const notUpdateError = 'Please update Development details for Handover to Factory';
	const notSimilarStatus = 'You have to select styles from similar status';

	const checkIfDisableClick = (name = '') => {
		if (isPatternMaster() || buttonNames?.includes(name)) {
			return false;
		}
		if (
			selectedIds[0]?.nextPendingActivity === 'APPROVED' &&
			replaceSpace(name)?.toUpperCase() === 'REJECTED'
		) {
			return false;
		}
		if (
			selectedIds[0]?.nextPendingActivity === 'REJECTED' &&
			selectedIds[0]?.currentActivity?.activityType === 'APPROVED'
		) {
			return true;
		}
		return checkIfSameSimilarStatus() !== replaceSpace(name)?.toUpperCase();
	};

	const renderActivityList = (statusList) =>
		statusList?.map((status, index) => (
			<button
				key={index}
				className={`button text size32 ${status === 'Delivered' && 'green'}`}
				onClick={() => handleClick(status)}
				disabled={checkIfDisableClick(status)}
			>
				<span>{status}</span>
			</button>
		));

	const getSameDevelopmenPoints = () => {
		const firstVal = selectedIds[0]?.unitResponse?.name;
		const differentVal = selectedIds?.find((item) => item.unitResponse?.name !== firstVal)?.unitResponse
			?.name;
		if (differentVal) {
			return 'DIFFERENT';
		} else {
			return firstVal;
		}
	};

	const checkIfAllUpdated = () => {
		const allUpdated = selectedIds.every((item) =>
			item?.unitResponse?.name === 'BD-Factory' ? item.fabricSupplier && item.developmentFactory : true
		);
		return allUpdated;
	};

	const checkIfSameSimilarStatus = () => {
		const firstVal = selectedIds[0]?.nextPendingActivity;
		const differentVal = selectedIds?.find(
			(item) => item.nextPendingActivity !== firstVal
		)?.nextPendingActivity;
		if (differentVal) {
			return 'DIFFERENT';
		} else {
			return firstVal;
		}
	};

	const renderPopupForOthers = () => {
		const pointsName = getSameDevelopmenPoints();
		if (pointsName === 'DIFFERENT')
			return (
				<span className="regular-14 red d-flex align-items-center mr-4">
					<img
						src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
						className="mr-2"
						alt="warning"
					/>{' '}
					{notSameError}
				</span>
			);
		else if (!checkIfAllUpdated())
			return (
				<span className="regular-14 red d-flex align-items-center mr-4">
					<img
						src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
						className="mr-2"
						alt="warning"
					/>
					{notUpdateError}
				</span>
			);
		else if (checkIfSameSimilarStatus() === 'DIFFERENT') {
			return (
				<span className="regular-14 red d-flex align-items-center mr-4">
					<img
						src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
						className="mr-2"
						alt="warning"
					/>
					{notSimilarStatus}
				</span>
			);
		} else if (['BD-Factory', 'Turkey-Factory'].includes(pointsName))
			if (checkPrimaryUserType('PRODUCT_DEVELOPMENT')) {
				return renderActivityList(['Handover to Factory', 'Received from Factory']);
			} else {
				return renderActivityList(factoryButtons);
			}
		else if (!['BD-Factory', 'Turkey-Factory'].includes(pointsName))
			if (checkPrimaryUserType('PRODUCT_DEVELOPMENT')) {
				return renderActivityList(buttonNames);
			} else {
				return renderActivityList(inhouseButtons);
			}
	};

	return (
		<>
			<div className="design-count-section bg-white collection-details-design-count shadow-8dp">
				<div className="count-info d-flex justify-content-between align-items-center">
					<div className="design-count">
						<p className="regular-14">{selectedIds.length}</p>
					</div>
					<div className="buttons">
						{isPatternMaster() ? renderActivityList(buttonNames) : renderPopupForOthers()}
					</div>
					<div className="text-right close-icon cursor-pointer">
						<img
							onClick={closeModal}
							src={`${process.env.PUBLIC_URL}/icons/close-icon.svg`}
							alt="close"
						/>
					</div>
				</div>
			</div>
			{openModal && ['Pattern', 'Cutting', 'Sewing'].includes(actionType) && (
				<AssignSampleMember
					selectedList={selectedIds}
					actionType={actionType}
					closeModal={handleClose}
				/>
			)}
		</>
	);
};

export default SampleActionPopup;
